import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchJobById } from '../../api/jobs';
import { Job, JobProduct } from '../../types/api';
import { getAllStorePhotos, clearStorePhotos } from '../../utils/inventoryStorage';
import api from '../../api/http';

interface ProductServiceProgress {
  productStatus: 'normal' | 'damaged' | 'not_found'; // 產品狀態（產品級別）
  productQuantity: number; // 產品數量（產品級別）
  services: {
    [serviceTypeCode: string]: {
      completed: boolean;
      hasPhotos: boolean;
      notes?: string;
    };
  };
  allCompleted: boolean;
  damagePhotoCount?: number;
}

interface StoreProgress {
  products: {
    [productId: string]: ProductServiceProgress;
  };
}

const StoreSubmitPage: React.FC = () => {
  const navigate = useNavigate();
  const { jobId, storeId } = useParams<{ jobId: string; storeId: string }>();
  
  const [serviceDate, setServiceDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [overallNotes, setOverallNotes] = useState<string>('');
  const [progress, setProgress] = useState<StoreProgress>({ products: {} });
  
  // 從服務器加載的草稿照片 URL
  const [serverPhotoUrls, setServerPhotoUrls] = useState<{ [productId: string]: { [key: string]: string[] } }>({});

  // 獲取 Job 詳情
  const {
    data: job,
    isLoading,
    isError,
  } = useQuery<Job>({
    queryKey: ['job', jobId],
    queryFn: () => fetchJobById(jobId as string),
    enabled: Boolean(jobId),
  });

  // 從服務器加載草稿進度和照片 URL（跨設備/帳戶共享）
  useEffect(() => {
    const loadFromServer = async () => {
      if (!jobId || !storeId) return;
      
      try {
        const response = await api.get('/service-drafts/by-store', {
          params: { job_id: jobId, store_id: storeId },
        });
        
        const drafts = response.data.drafts;
        
        if (drafts && Object.keys(drafts).length > 0) {
          // 從服務器草稿構建進度數據
          const serverProgress: StoreProgress = { products: {} };
          const photoUrlsMap: { [productId: string]: { [key: string]: string[] } } = {};
          
          // 為每個產品獲取詳細的照片 URL
          for (const [productId, draft] of Object.entries(drafts) as [string, any][]) {
            // 從 services_data 構建服務進度
            const services: { [key: string]: { completed: boolean; hasPhotos: boolean; notes?: string } } = {};
            
            // 先初始化 services_data 中的服務
            if (draft.services_data) {
              Object.entries(draft.services_data).forEach(([serviceCode, serviceData]: [string, any]) => {
                services[serviceCode] = {
                  completed: serviceData.completed || false,
                  hasPhotos: serviceData.hasPhotos || false,
                  notes: serviceData.notes,
                };
              });
            }
            
            // 根據照片數據來確定服務狀態
            if (draft.photos_data) {
              // 收集每個服務的前後照狀態
              const servicePhotos: { [serviceCode: string]: { hasBefore: boolean; hasAfter: boolean } } = {};
              
              Object.keys(draft.photos_data).forEach(key => {
                const photos = draft.photos_data[key];
                if (photos && photos.length > 0) {
                  // key 格式: "cleaning_before", "pop_replacement_after"
                  const lastUnderscoreIndex = key.lastIndexOf('_');
                  if (lastUnderscoreIndex > 0) {
                    const serviceCode = key.substring(0, lastUnderscoreIndex);
                    const photoType = key.substring(lastUnderscoreIndex + 1);
                    
                    if (!servicePhotos[serviceCode]) {
                      servicePhotos[serviceCode] = { hasBefore: false, hasAfter: false };
                    }
                    
                    if (photoType === 'before') {
                      servicePhotos[serviceCode].hasBefore = true;
                    } else if (photoType === 'after') {
                      servicePhotos[serviceCode].hasAfter = true;
                    }
                  }
                }
              });
              
              // 根據前後照狀態更新服務狀態
              Object.entries(servicePhotos).forEach(([serviceCode, photoStatus]) => {
                const hasPhotos = photoStatus.hasBefore && photoStatus.hasAfter;
                const completed = hasPhotos;
                
                if (!services[serviceCode]) {
                  services[serviceCode] = { completed: false, hasPhotos: false };
                }
                
                services[serviceCode].hasPhotos = hasPhotos;
                services[serviceCode].completed = completed;
              });
            }
            
            // 檢查是否所有服務都完成
            // 如果產品不存在，直接算作完成
            const productStatus = draft.product_status || 'normal';
            const allServicesCompleted = productStatus === 'not_found' 
              ? true 
              : Object.values(services).every(s => s.completed && s.hasPhotos);
            
            serverProgress.products[productId] = {
              productStatus,
              productQuantity: draft.product_quantity || 1,
              services,
              allCompleted: allServicesCompleted,
            };
            
            // 獲取照片 URL
            try {
              const photoResponse = await api.get('/service-drafts/get', {
                params: { job_id: jobId, store_id: storeId, product_id: productId },
              });
              
              const { photos_urls } = photoResponse.data;
              if (photos_urls) {
                photoUrlsMap[productId] = photos_urls;
              }
            } catch (error) {
              console.error(`加載產品 ${productId} 的照片 URL 失敗:`, error);
            }
          }
          
          setProgress(serverProgress);
          setServerPhotoUrls(photoUrlsMap);
        } else {
          // 沒有服務器草稿，嘗試從 localStorage 加載
          const key = `job_${jobId}_store_${storeId}_progress`;
          const saved = localStorage.getItem(key);
          if (saved) {
            try {
              setProgress(JSON.parse(saved));
            } catch (e) {
              console.error('Failed to parse progress:', e);
            }
          }
        }
      } catch (error) {
        console.error('從服務器加載進度失敗:', error);
        // 失敗時回退到 localStorage
        const key = `job_${jobId}_store_${storeId}_progress`;
        const saved = localStorage.getItem(key);
        if (saved) {
          try {
            setProgress(JSON.parse(saved));
          } catch (e) {
            console.error('Failed to parse progress:', e);
          }
        }
      }
    };
    
    loadFromServer();
  }, [jobId, storeId]);

  // 批量提交服務記錄
  const submitMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post('/service-records/batch', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    },
    onSuccess: () => {
      // 清除 localStorage 和內存中的照片
      if (jobId && storeId) {
        const key = `job_${jobId}_store_${storeId}_progress`;
        localStorage.removeItem(key);
        clearStorePhotos(Number(jobId), Number(storeId));
      }
      // 返回 Job 詳情頁
      navigate(`/jobs/${jobId}`);
    },
  });

  const handleSubmit = async () => {
    if (!jobId || !storeId || !job) {
      alert('缺少必要信息');
      return;
    }

    // ⭐ 最終檢查：確保所有服務都有前後照
    const missingPhotosProducts: string[] = [];
    
    Object.entries(progress.products).forEach(([productId, productProgress]) => {
      if (productProgress.productStatus === 'not_found') {
        return; // 產品不存在時跳過檢查
      }
      
      // 檢查每個服務是否都有前後照
      Object.entries(productProgress.services).forEach(([serviceCode, serviceData]) => {
        if (!serviceData.completed || !serviceData.hasPhotos) {
          const product = job?.job_products?.find(
            (jp: JobProduct) => jp.product_id === Number(productId) && jp.store_id === Number(storeId)
          );
          const productName = product?.product?.name || `產品 ${productId}`;
          const serviceName = serviceCode === 'inventory' ? '盤點' : 
                             serviceCode === 'pop_replacement' ? 'POP 更換' : 
                             serviceCode === 'cleaning' ? '清潔' : serviceCode;
          
          missingPhotosProducts.push(`${productName} - ${serviceName}`);
        }
      });
    });

    if (missingPhotosProducts.length > 0) {
      alert(`以下服務項目缺少前後照片，無法提交：\n\n${missingPhotosProducts.join('\n')}\n\n請返回完成所有照片拍攝後再提交。`);
      return;
    }

    // 構建 FormData
    const formData = new FormData();
    formData.append('job_id', jobId);
    formData.append('store_id', storeId);
    formData.append('service_date', serviceDate);
    if (overallNotes) {
      formData.append('notes', overallNotes);
    }

    // 從內存獲取所有照片（作為備用）
    const allPhotos = getAllStorePhotos(Number(jobId), Number(storeId));
    let photoIndex = 0;

    // 為每個產品的每個服務創建記錄
    let recordIndex = 0;
    let damagePhotoIndex = 0;
    
    Object.entries(progress.products).forEach(([productId, productProgress]) => {
        // 處理產品損壞照片（如果有）
        if (productProgress.productStatus === 'damaged') {
          const damageKey = `photos_${jobId}_${storeId}_${productId}_damage`;
          const damagePhotos = allPhotos.get(damageKey) || [];
        
        damagePhotos.forEach((file) => {
          formData.append(`damage_photos[${damagePhotoIndex}][file]`, file);
          formData.append(`damage_photos[${damagePhotoIndex}][product_id]`, productId);
          formData.append(`damage_photos[${damagePhotoIndex}][photo_order]`, String(damagePhotoIndex));
          damagePhotoIndex++;
        });
      }
      
      Object.entries(productProgress.services).forEach(([serviceCode, serviceData]) => {
        // 添加服務記錄基本信息
        formData.append(`records[${recordIndex}][product_id]`, productId);
        formData.append(`records[${recordIndex}][service_type]`, serviceCode);
        
        // 添加產品級別的數量和狀態
        formData.append(`records[${recordIndex}][quantity_found]`, String(productProgress.productQuantity));
        formData.append(`records[${recordIndex}][product_status]`, productProgress.productStatus);
        
        // 查找對應的 job_service_item_id
        const serviceItem = job.service_items?.find((si: any) => si.service_type === serviceCode);
        if (serviceItem) {
          formData.append(`records[${recordIndex}][job_service_item_id]`, String(serviceItem.id));
        }

        // 添加備註
        if (serviceData.notes) {
          formData.append(`records[${recordIndex}][notes]`, serviceData.notes);
        }

        // 添加照片
        const beforeKey = `photos_${jobId}_${storeId}_${productId}_${serviceCode}_before`;
        const afterKey = `photos_${jobId}_${storeId}_${productId}_${serviceCode}_after`;
        
        const beforePhotos = allPhotos.get(beforeKey) || [];
        const afterPhotos = allPhotos.get(afterKey) || [];

        beforePhotos.forEach((file) => {
          formData.append(`records[${recordIndex}][photos][${photoIndex}][file]`, file);
          formData.append(`records[${recordIndex}][photos][${photoIndex}][photo_type]`, 'before');
          formData.append(`records[${recordIndex}][photos][${photoIndex}][photo_order]`, String(photoIndex));
          photoIndex++;
        });

        afterPhotos.forEach((file) => {
          formData.append(`records[${recordIndex}][photos][${photoIndex}][file]`, file);
          formData.append(`records[${recordIndex}][photos][${photoIndex}][photo_type]`, 'after');
          formData.append(`records[${recordIndex}][photos][${photoIndex}][photo_order]`, String(photoIndex));
          photoIndex++;
        });

        recordIndex++;
      });
    });

    submitMutation.mutate(formData);
  };

  // 計算統計數據
  const stats = React.useMemo(() => {
    const totalProducts = Object.keys(progress.products).length;
    const completedProducts = Object.values(progress.products).filter(p => p.allCompleted).length;
    const totalServices = Object.values(progress.products).reduce(
      (sum, p) => sum + Object.keys(p.services).length, 
      0
    );
    const completedServices = Object.values(progress.products).reduce(
      (sum, p) => sum + Object.values(p.services).filter(s => s.completed).length, 
      0
    );
    
    return { totalProducts, completedProducts, totalServices, completedServices };
  }, [progress]);

  // 獲取產品詳情
  const getProductDetails = (productId: string) => {
    const jobProduct = job?.job_products?.find(
      (jp: JobProduct) => jp.product_id === Number(productId) && jp.store_id === Number(storeId)
    );
    return jobProduct?.product;
  };

  // 獲取照片預覽 URL（優先使用服務器的照片，然後是內存中的照片）
  const getPhotoPreviewUrls = (productId: string, serviceCode: string, photoType: 'before' | 'after') => {
    if (!jobId || !storeId) return [];
    
    // 優先使用服務器的照片 URL
    const serverKey = `${serviceCode}_${photoType}`;
    const serverUrls = serverPhotoUrls[productId]?.[serverKey] || [];
    if (serverUrls.length > 0) {
      return serverUrls;
    }
    
    // 回退到內存中的照片
    const allPhotos = getAllStorePhotos(Number(jobId), Number(storeId));
    const memoryKey = `photos_${jobId}_${storeId}_${productId}_${serviceCode}_${photoType}`;
    const files = allPhotos.get(memoryKey) || [];
    return files.map(file => URL.createObjectURL(file));
  };

  // 獲取損壞照片預覽 URL（優先使用服務器的照片）
  const getDamagePhotoPreviewUrls = (productId: string) => {
    if (!jobId || !storeId) return [];
    
    // 優先使用服務器的照片 URL
    const serverUrls = serverPhotoUrls[productId]?.['damage'] || [];
    if (serverUrls.length > 0) {
      return serverUrls;
    }
    
    // 回退到內存中的照片
    const allPhotos = getAllStorePhotos(Number(jobId), Number(storeId));
    const memoryKey = `photos_${jobId}_${storeId}_${productId}_damage`;
    const files = allPhotos.get(memoryKey) || [];
    return files.map(file => URL.createObjectURL(file));
  };

  // 獲取當前店鋪資訊
  const currentStore = React.useMemo(() => {
    return job?.stores?.find((s: any) => s.id === Number(storeId));
  }, [job, storeId]);

  if (isLoading) {
    return <div className="panel">載入中...</div>;
  }

  if (isError || !job) {
    return (
      <div className="panel">
        <p>無法載入工作資料</p>
        <button onClick={() => navigate(`/jobs/${jobId}`)}>返回工作詳情</button>
      </div>
    );
  }

  return (
    <div className="panel">
      <button
        className="link-back"
        onClick={() => navigate(`/jobs/${jobId}/store/${storeId}/products`)}
        style={{ display: 'inline-block', marginBottom: '1rem', fontSize: '1.125rem', fontWeight: '600' }}
      >
        ← 返回產品列表
      </button>

      <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '1rem' }}>
        提交服務記錄
      </h2>

      {/* 店鋪資訊 */}
      <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>
          {currentStore?.store_code}
        </h3>
        <p style={{ fontSize: '1.125rem', color: '#6b7280', fontWeight: 500 }}>
          {currentStore?.retailer?.name}
        </p>
        {currentStore?.building && (
          <p style={{ fontSize: '1.125rem', color: '#6b7280', marginTop: '0.5rem', fontWeight: 500 }}>
            {currentStore.building.building_name} {currentStore.floor} {currentStore.unit}
          </p>
        )}
      </div>

      {/* 進度摘要 */}
      <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
        <h3 style={{ fontSize: '1.375rem', fontWeight: 700, marginBottom: '1rem' }}>
          服務摘要
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
          <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px' }}>
            <p style={{ fontSize: '1rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.5rem' }}>
              完成產品
            </p>
            <p style={{ fontSize: '1.75rem', fontWeight: 700, color: '#2563eb' }}>
              {stats.completedProducts} / {stats.totalProducts}
            </p>
          </div>
          <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '6px' }}>
            <p style={{ fontSize: '1rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.5rem' }}>
              完成服務
            </p>
            <p style={{ fontSize: '1.75rem', fontWeight: 700, color: '#2563eb' }}>
              {stats.completedServices} / {stats.totalServices}
            </p>
          </div>
        </div>
      </div>

      {/* 產品詳情列表 */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.375rem', fontWeight: 700, marginBottom: '1rem' }}>
          服務詳情
        </h3>
        <div style={{ display: 'grid', gap: '1rem' }}>
          {Object.entries(progress.products).map(([productId, productProgress]) => {
            const product = getProductDetails(productId);
            if (!product) return null;

            const damagePhotoUrls = getDamagePhotoPreviewUrls(productId);

            const productStatus = productProgress.productStatus;
            const isDamaged = productStatus === 'damaged';
            const isNotFound = productStatus === 'not_found';

            return (
              <div
                key={productId}
                style={{
                  padding: '1.5rem',
                  border: '2px solid',
                  borderColor: isDamaged ? '#fca5a5' : isNotFound ? '#d1d5db' : '#e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: isDamaged ? '#fef2f2' : isNotFound ? '#f9fafb' : 'white',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <h4 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>
                    {product.name}
                  </h4>
                  {isDamaged && (
                    <span style={{ fontSize: '1.125rem', color: '#dc2626', fontWeight: 700 }}>
                      ⚠️ 損壞
                    </span>
                  )}
                  {isNotFound && (
                    <span style={{ fontSize: '1.125rem', color: '#6b7280', fontWeight: 700 }}>
                      ❌ 不存在
                    </span>
                  )}
                </div>
                {product.model && (
                  <p style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 500 }}>
                    型號：{product.model}
                  </p>
                )}
                
                {/* 產品數量 - 產品級別 */}
                <p style={{ fontSize: '1.125rem', color: '#1f2937', marginBottom: '1rem', fontWeight: 700 }}>
                  數量：{productProgress.productQuantity}
                </p>

                {/* 損壞照片預覽 */}
                {damagePhotoUrls.length > 0 && (
                  <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fee2e2', borderRadius: '6px' }}>
                    <h5 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#dc2626', marginBottom: '0.75rem' }}>
                      損壞照片 ({damagePhotoUrls.length} 張)
                    </h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.5rem' }}>
                      {damagePhotoUrls.map((url, index) => (
                        <img
                          key={index}
                          src={url}
                          alt={`損壞照片 ${index + 1}`}
                          style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #fca5a5' }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ marginLeft: '1rem' }}>
                  {Object.entries(productProgress.services).map(([serviceCode, serviceData]) => {
                    const beforePhotoUrls = getPhotoPreviewUrls(productId, serviceCode, 'before');
                    const afterPhotoUrls = getPhotoPreviewUrls(productId, serviceCode, 'after');
                    const totalPhotos = beforePhotoUrls.length + afterPhotoUrls.length;

                    return (
                      <div key={serviceCode} style={{ marginBottom: '0.75rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '1.125rem', fontWeight: 600 }}>
                            {serviceCode === 'inventory' ? '盤點' : 
                             serviceCode === 'pop_replacement' ? 'POP 更換' : 
                             serviceCode === 'cleaning' ? '清潔' : serviceCode}
                          </span>
                          <span style={{ fontSize: '1.25rem', color: serviceData.completed ? '#10b981' : '#9ca3af' }}>
                            {serviceData.completed ? '✓' : '○'}
                          </span>
                        </div>

                        {serviceData.notes && (
                          <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#fffbe0', borderRadius: '4px', border: '1px solid #fcd34d' }}>
                            <p style={{ margin: 0, fontSize: '0.9rem', color: '#92400e' }}>
                              備註：{serviceData.notes}
                            </p>
                          </div>
                        )}

                        {totalPhotos > 0 && (
                          <div style={{ marginTop: '0.75rem' }}>
                            <p style={{ fontSize: '1rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.5rem' }}>
                              照片 ({totalPhotos} 張)
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '0.5rem' }}>
                              {beforePhotoUrls.map((url, index) => (
                                <div key={`before-${index}`}>
                                  <img
                                    src={url}
                                    alt={`前照 ${index + 1}`}
                                    style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                                  />
                                  <p style={{ fontSize: '0.75rem', textAlign: 'center', marginTop: '0.25rem', fontWeight: 600, color: '#6b7280' }}>前</p>
                                </div>
                              ))}
                              {afterPhotoUrls.map((url, index) => (
                                <div key={`after-${index}`}>
                                  <img
                                    src={url}
                                    alt={`後照 ${index + 1}`}
                                    style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                                  />
                                  <p style={{ fontSize: '0.75rem', textAlign: 'center', marginTop: '0.25rem', fontWeight: 600, color: '#6b7280' }}>後</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 服務日期 */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label className="form-label" style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
          服務日期 *
        </label>
        <input
          type="date"
          value={serviceDate}
          onChange={(e) => setServiceDate(e.target.value)}
          required
          className="form-input"
          style={{ fontSize: '1.125rem', fontWeight: '500', padding: '0.75rem' }}
        />
      </div>

      {/* 整體備註 */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label className="form-label" style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
          整體備註
        </label>
        <textarea
          value={overallNotes}
          onChange={(e) => setOverallNotes(e.target.value)}
          rows={4}
          className="form-textarea"
          placeholder="輸入整體備註..."
          style={{ fontSize: '1.125rem', fontWeight: '500', padding: '0.75rem' }}
        />
      </div>

      {/* 錯誤顯示 */}
      {submitMutation.isError && (
        <div
          className="form-error"
          style={{
            padding: '1rem',
            backgroundColor: '#fee2e2',
            borderRadius: '6px',
            marginBottom: '1rem',
            fontSize: '1rem',
            fontWeight: '600',
          }}
        >
          <strong>提交失敗：</strong>
          {(submitMutation.error as any)?.response?.data?.message ||
            (submitMutation.error as Error)?.message ||
            '請重試'}
        </div>
      )}

      {/* 操作按鈕 */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
        <button
          type="button"
          onClick={() => navigate(`/jobs/${jobId}/store/${storeId}/products`)}
          className="btn-secondary"
          style={{ flex: 1, fontSize: '1.125rem', fontWeight: '700', padding: '0.75rem 1.5rem' }}
        >
          取消
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitMutation.isPending || stats.completedProducts !== stats.totalProducts}
          className="btn-primary"
          style={{
            flex: 1,
            fontSize: '1.125rem',
            fontWeight: '700',
            padding: '0.75rem 1.5rem',
            opacity: submitMutation.isPending || stats.completedProducts !== stats.totalProducts ? 0.5 : 1,
            cursor: submitMutation.isPending || stats.completedProducts !== stats.totalProducts ? 'not-allowed' : 'pointer',
          }}
        >
          {submitMutation.isPending ? '提交中...' : '提交所有服務記錄'}
        </button>
      </div>
    </div>
  );
};

export default StoreSubmitPage;

